import VectorSource from "ol/source/Vector";
import { Geometry3 } from "../geom";
import { isDefined, isObject } from "../utils/utils";
import { getDefaultEditStyleFunction } from "./styles/defaults";
import { ILayer, ILayerOptions, ILayerSettings, IMap, IMapLabelOptions, OpenLayersStyleProvider } from "./types";
import { createFeatures, getExtentsUnion } from "./utils";
import { ol_Extent, ol_Feature, ol_source, ol_style, ol_geom, ol_layer } from "@omnia/exports/openlayers";


const defaultProjection = "EPSG:3857";

export function getExtentForLayers(layers: Layer[]): ol_Extent {
    const extents = layers.map((l) => l.getExtent()).filter((e) => !!e);
    return getExtentsUnion(extents);
}

export class Layer<Settings extends ILayerSettings = ILayerSettings> implements ILayer {
    protected _genericLayer: ol_layer.Layer;

    protected _layer: ol_layer.Vector;
    protected _source: ol_source.Vector;

    private static defaultStyles: OpenLayersStyleProvider[] = [];

    private _showLabels: boolean = true;

    /**
     * If true, features in this layer can be selected.
     *
     * @default true
     */
    public selectable: boolean = true;

    public styleProvider: (selected: boolean) => OpenLayersStyleProvider;

    protected defaultStyleProvider: (selected: boolean) => OpenLayersStyleProvider;

    public fillSelectedStyle: boolean = true;

    /**
     * If true, this layer will appear in the "layers" popup displayed at the bottom left of maps.
     *
     * @default false
     */
    public layersPopup: boolean = false;

    /**
     * If true, this layer should always be visible, and cannot be hidden by users
     * This impacts only the user interface - layers with `alwaysVisible == true` can still be hidden by program
     *
     * @default false
     */
    public alwaysVisible: boolean = false;

    protected _highlightFeatures: Set<string | number | ol_Feature>;

    protected _hiddenFeatures: ol_Feature[];

    protected settings: Settings;

    protected _labelOptions: IMapLabelOptions = { declutter: true, deduplicate: true };

    constructor(options: ILayerOptions = {}) {
        this.defaultStyleProvider = options?.defaultStyleProvider;

        this.layersPopup = options?.layersPopup ?? false;

        this.selectable = options?.selectable ?? true;

        // Set defaults from options.
        this._labelOptions = {
            ...this._labelOptions,
            ...(options?.labelOptions ?? {}),
        };

        this.settings = this.getDefaultSettings(options);

        this.alwaysVisible = this.settings.alwaysVisible ?? false;

        this.createLayer();

        this.restoreSettings();
    }

    // 20241015 - These types & the type cast aren't correct, but correcting them involves rearranging the super/child relationships.
    protected getDefaultSettings(options?: ILayerOptions): Settings {
        const settings = {
            visible: true,
            showLabels: true,
            ...(options?.settings ?? {}),
        } as Settings;

        return settings;
    }

    protected createLayer() {

        const source = this._source ?? new ol_source.Vector();

        const options = {
            source,
            style: this.getLayerStyleFunctionInternal(false),
            declutter: this._labelOptions.declutter,
        }

        this._source = options.source;
        this._genericLayer = this._layer = new ol_layer.Vector(options);
    }

    protected restoreSettings() {
        const { visible, showLabels } = this.settings;

        if (this.visible != visible) {
            this.visible = visible;
        }

        if (this.showLabels != showLabels) {
            this.showLabels = showLabels;
        }
    }

    getConfigurableLayers(): ILayer[] {
        return [this];
    }

    protected clusterSource(cluster: boolean) {
        if (cluster) {
            var c = new ol_source.Cluster({
                source: this._source,
                distance: 10,
            });

            this._layer.setSource(c);
        } else {
            this._layer.setSource(this._source);
        }
    }

    refresh() {
        this._layer.setStyle(null);
        this._layer.setStyle(this.getLayerStyleFunctionInternal(false));
    }

    get name(): string {
        return null;
    }

    get description(): string {
        return this.name;
    }

    get mapLayer() {
        return this._layer;
    }

    get isVisible(): boolean {
        return this._genericLayer.getVisible();
    }

    get visible(): boolean {
        return this._genericLayer.getVisible();
    }

    set isVisible(value: boolean) {
        this.setVisible(value);
    }

    set visible(value: boolean) {
        this.setVisible(value);
    }

    protected setVisible(value: boolean) {
        this._genericLayer.setVisible(value);
        this.settings.visible = value;
    }

    get opacity(): number {
        return this._genericLayer.getOpacity() * 100;
    }

    set opacity(value: number) {
        this._genericLayer.setOpacity(value / 100);
    }

    get showLabels(): boolean {
        return this._showLabels;
    }

    set showLabels(value: boolean) {
        if (this._showLabels != value) {
            this._showLabels = value;
            this.settings.showLabels = value;
            this.refresh();
        }
    }

    getFeatures(): ol_Feature[] {
        return this._source.getFeatures();
    }

    get featuresCount(): number {
        var count = 0;
        this._source.forEachFeature((f) => {
            count++;
            return false;
        });
        return count;
    }

    public addToMap(map: IMap) {
        const olmap = map.getMap();
        olmap.addLayer(this._genericLayer);
    }

    public removeFromMap(map: IMap) {
        let olmap = map.getMap();
        olmap.removeLayer(this._genericLayer);
    }

    protected recreateLayer(map: IMap) {
        const current = this._genericLayer;
        this.createLayer();

        const olmap = map.getMap();
        const layers = olmap.getLayers();

        let index = -1;
        layers.forEach((l, i) => (index = l == current ? i : index));

        this.removeFromMap(map);
        if (index >= 0) {
            layers.setAt(index, this._genericLayer);
        } else {
            this.removeFromMap(map);
            this.addToMap(map);
        }
    }

    public setSource<T>(
        source: T[],
        geometrySelector: (i: T) => Geometry3,
        idSelector?: (i: T) => any,
        valueSelector?: (i: T) => any
    ) {
        const features = this.createFeatures(source, geometrySelector, idSelector, valueSelector);

        return this.setFeatures(features);
    }

    setFeatures(features: ol_Feature[]) {
        this._source.clear();
        this._source.addFeatures(features);

        return features;
    }

    public createNewFeature(geometry: ol_geom.Geometry, data: any): ol_Feature {
        let feature: ol_Feature;
        var id = this.getMaxFeatureId() + 1;

        if (!geometry) {
            feature = new ol_Feature();
            feature.set("layer", this);
            feature.set("data", data);
        } else {
            feature = this.createFeature(
                data,
                (z) => geometry,
                (z) => id
            );
        }

        this._source.addFeatures([feature]);

        return feature;
    }

    createFeatures<T>(
        source: T[],
        geometrySelector: (i: T) => Geometry3,
        idSelector?: (i: T) => any,
        valueSelector?: (i: T) => any
    ): ol_Feature[] {
        const featureData = {
            layer: this,
        };

        return createFeatures(source, geometrySelector, idSelector, valueSelector, defaultProjection, featureData);
    }

    protected createFeature<T>(
        source: T,
        geometrySelector: (i: T) => Geometry3,
        idSelector: (i: T) => any,
        valueSelector?: (i: T) => any
    ): any {
        return this.createFeatures([source], geometrySelector, idSelector, valueSelector)[0];
    }

    getExtent(): ol_Extent {
        if (this.featuresCount > 0) {
            return this._source.getExtent();
        }
    }

    private getLayerStyleFunctionInternal(selected: boolean): OpenLayersStyleProvider {
        return (feature, resolution) => {
            if (this._hiddenFeatures && this._hiddenFeatures.indexOf(<ol_Feature>feature) >= 0) {
                return null;
            }

            return this.getLayerStyleFunction(selected)(feature, resolution);
        };
    }

    getLayerStyleFunction(selected: boolean): OpenLayersStyleProvider {
        const provider = this.styleProvider || this.defaultStyleProvider || Layer.getLayerStyleFunction;

        return provider(selected);
    }

    getEditStyleFunction(feature: ol_Feature) {
        return getDefaultEditStyleFunction(feature);
    }

    static getLayerStyleFunction(selected): OpenLayersStyleProvider {
        if (!Layer.defaultStyles[selected]) {
            Layer.defaultStyles[selected] = Layer.getDefaultStyle(selected);
        }

        return Layer.defaultStyles[selected];
    }

    private static getDefaultStyle(selected: boolean): OpenLayersStyleProvider {
        return (feature: ol_Feature, resolution) => [
            new ol_style.Style({
                fill: new ol_style.Fill({ color: [255, 255, 255, selected ? 0.5 : 0.25] }),

                stroke: new ol_style.Stroke({
                    color: [0, 0, 0, 1],
                    width: selected ? 5 : 1,
                }),
            }),
        ];
    }

    findFeatures(predicate: (feature: ol_Feature, data: any) => boolean): ol_Feature[] {
        const features = this._source.getFeatures();

        return features.filter((f) => predicate(f, f.get("data")));
    }

    findFeaturesWithData(items: any[]): ol_Feature[] {
        return this.findFeatures((f) => {
            var data = f.get("data");

            if (isDefined(data)) {
                return items.indexOf(data) >= 0;
            }

            return false;
        });
    }

    static getDataFromFeatures<T>(features: ol_Feature[]) {
        return features.map((f) => <T>f.get("data")).filter((d) => isDefined(d));
    }

    getDataFromFeatures<T>() {
        return Layer.getDataFromFeatures<T>(this._source.getFeatures());
    }

    public removeFeature(feature: ol_Feature, check: boolean = false) {
        const remove = !check || (feature && this._source.getFeatures().some((f) => f == feature));

        if (remove) {
            this._source.removeFeature(feature);
        }
    }

    protected getMaxFeatureId(): number {
        var max = Number.MIN_VALUE;

        this._source.forEachFeature((f) => {
            var id = f.getId();
            if (typeof id === "number") {
                if (max < id) max = id;
            }
        });

        return max;
    }

    public setHighlightFeatures(features: (string | number | ol_Feature)[]) {
        this._highlightFeatures = features ? new Set(features) : undefined;
        this.refresh();
    }

    public setHiddenFeatures(features: any[]) {
        this._hiddenFeatures = features;
        this.refresh();
    }

    isFeatureHighlighted(feature: ol_Feature) {
        if (this._highlightFeatures == null) return null;
        if (!feature) return null;
        if (this._highlightFeatures.has(feature)) return true;
        if (this._highlightFeatures.has(feature.getId())) return true;
        return false;
    }

    protected highlightFeaturesWithData<T>(data: T[], id: (item: T) => string | number) {
        data = (data || []).filter((d) => !!d);

        if (!data) {
            this.clearHighlight();
            return;
        }

        const keys = data && id ? data.map(id) : this.findFeaturesWithData(data);

        this.setHighlightFeatures(keys);
    }

    clearHighlight() {
        this.setHighlightFeatures(null);
    }

    public static getSelectedStyle(feature, resolution) {
        let layer = <Layer>feature.get("layer");

        if (isObject(layer)) {
            var styleFunction = layer.getLayerStyleFunction(true);

            if (styleFunction) {
                return styleFunction(feature, resolution);
            }
        }
    }

    checkResolutionLabelFilter(resolution, maxResolution = 5) {
        return resolution <= maxResolution;
    }

    getFeatureLabelVisibility(feature: any): boolean {
        return feature && !feature.get("hideLabel");
    }

    public findFeaturesInExtent(extent: ol_Extent): ol_Feature[] {
        return this._source.getFeaturesInExtent(extent);
    }

    public findItemsInExtent<T>(extent: ol_Extent): T[] {
        let features = this._source.getFeaturesInExtent(extent);
        return features.map((f) => <T>f.get("data"));
    }

    public getSnappingFeatures(): ol_Feature[] {
        return this._source.getFeatures();
    }

    public updateLabelOptions(map: IMap, labels: IMapLabelOptions) {
        const current = this._labelOptions;

        this._labelOptions = labels;

        if (current.declutter != labels.declutter) {
            this.recreateLayer(map);
        }

        this.refresh();
    }
}
